import { useState, useEffect } from 'react';
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { DashboardView } from './components/DashboardView';
import { ReportsViewNew } from './components/ReportViewNew';
import { ComponentShowcase } from './components/ComponentShowcase';
import { ZeiterfassungWrapper } from './components/timetracking/ZeiterfassungWrapper';
import { UserRequestsView } from './components/UserRequestsView';
import { UserSettingsView } from './components/UserSettingsView';
import { UserProfileView } from './components/UserProfileView';
import { PayrollView } from './components/admin/PayrollView';
import { MonthClosureView } from './components/admin/MonthClosureView';
import { UserManagementView } from './components/admin/UserManagementView';
import { VehiclesView } from './components/admin/VehiclesView';
import { HolidaysView } from './components/admin/HolidaysView';
import { AdminUserView } from './components/admin/AdminUserView';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { TimeOverviewView } from './components/admin/TimeOverviewView';
import { SystemView } from './components/admin/SystemView';
import { LoginPage } from './components/LoginPage';
import { AuthProvider } from './contexts/authContext';
import { useAuth } from './graphql/hooks/useAuth';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { FirstLogin } from './components/FirstLogin';
import { Routes, Route } from "react-router-dom";
import useMakeScrollable  from './components/custome_hooks/scrollable'
import { AppsView } from './components/AppsView';
import { useCheckToken } from './graphql/hooks/useUserDashboard';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { Toaster } from "react-hot-toast";
import { useDeviceToken } from './custom_hooks/useDeviceToken'


function AppContent() {
  useDeviceToken();
  useMakeScrollable();
  const { token, userType, userName, signOut } = useAuth();
  const [firstLoginToken, setFirstLoginToken] = useState<string | null>(null);
  const [activePage, setActivePage] = useState('zeiterfassung');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  
  const {
    data: isValid,
    isLoading,
    isError
  } = useCheckToken({
    enabled: !!token
  });

  useEffect(() => {
    if (!token) return;

    // Wait until validation finished
    if (isLoading) return;

    // If token invalid OR request failed → logout immediately
    if (isValid === false || isError) {
      signOut();
    }
  }, [token, isValid, isError, isLoading, signOut]);

  // Listen for navigation events from components
  useEffect(() => {
    const handleNavigate = (event: any) => {
      if (event.detail && event.detail.page) {
        setActivePage(event.detail.page);
      }
    };

    window.addEventListener('navigate', handleNavigate);
    return () => window.removeEventListener('navigate', handleNavigate);
  }, []);

  // Route protection: redirect to appropriate page if accessing unauthorized route
  useEffect(() => {
    if (!userType) return;

    const adminOnlyPages = ['admin-dashboard', 'time-overview', 'payroll', 'month-closure', 'user-management', 'vehicles', 'holidays', 'sick-leave', 'system', 'security', 'anträge'];

    // Regular users cannot access admin-only pages (but can access 'admin' overview)
    if (userType === 'user' && adminOnlyPages.includes(activePage)) {
      setActivePage('zeiterfassung');
    }
  }, [activePage, userType]);

  // Handle settings navigation
  const handleSettingsClick = () => {
    setActivePage('settings');
  };

  // Handle profile navigation
  const handleProfileClick = () => {
    setActivePage('profile');
  };

  // Handle navigation to request detail from notification
  const handleNavigateToRequest = (requestId: string) => {
    // Navigate to the appropriate requests page based on view mode
    if (userType === 'admin') {
      setActivePage('anträge');
    } else {
      setActivePage('meine-anträge');
    }
    
    // Dispatch event with request ID for the requests view to handle
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openRequestDetail', { 
        detail: { requestId } 
      }));
    }, 100);
  };

  // Handle navigation to admin page from notification
  const handleNavigateToPage = (page: string) => {
    setActivePage(page);
  };

  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    if (path === '/auth/first-login') {
      const token = params.get('token');

      if (token) {
        setFirstLoginToken(token);
      }
    }
  }, []);


  if (!token) {
    return <LoginPage />;
  }

  const getPageTitle = () => {
    switch (activePage) {
      case 'dashboard':
        return 'Dashboard';
      case 'admin-dashboard':
        return 'Admin Dashboard';
      case 'zeiterfassung':
        return 'Zeiterfassung';
      case 'auswertung':
        return 'Auswertung';
      case 'anträge':
        return 'Anträge';
      case 'meine-anträge':
        return 'Meine Anträge';
      case 'admin':
        return 'Admin-Bereich';
      case 'time-overview':
        return 'Zeitübersicht';
      case 'payroll':
        return 'Lohnbüro';
      case 'month-closure':
        return 'Monatsabschlüsse';
      case 'user-management':
        return 'Benutzerverwaltung';
      case 'vehicles':
        return 'Fahrzeuge';
      case 'holidays':
        return 'Feiertage';
      case 'sick-leave':
        return 'Krankmeldungen';
      case 'system':
        return 'System';
      case 'security':
        return 'Sicherheit';
      case 'settings':
        return 'Einstellungen';
      case 'profile':
        return 'Mein Profil';
      case 'code':
        return 'Component Showcase';
      default:
        return 'Dashboard';
    }
  };

  const getCurrentDate = () => {
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    const date = new Date(); 
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${dayName}, ${day}. ${month} ${year}`;
  };

  // Check if current page is accessible for the user
  const isPageAccessible = (page: string) => {
    // Admin-only pages that require admin role
    const adminOnlyPages = ['admin', 'anträge', 'admin-dashboard', 'time-overview', 'payroll', 'month-closure', 'user-management', 'vehicles', 'holidays', 'sick-leave', 'system', 'security'];
    
    // Regular users cannot access admin pages
    if (userType === 'user' && adminOnlyPages.includes(page)) {
      return false;
    }
    return true;
  };



  if (firstLoginToken) {
    return <FirstLogin />;
  }

  return (
      <div className="flex h-screen overflow-hidden bg-[#f5f5f5]">
        <Sidebar 
          activePage={activePage} 
          onPageChange={setActivePage} 
        />
        <div className="flex-1 flex flex-col overflow-hidden mx-2">
          <TopBar
            pageTitle={getPageTitle()}
            dateContext={getCurrentDate()}
            userName={userName || 'User'}
            userRole={userType || 'user'}
            onSettingsClick={handleSettingsClick}
            onProfileClick={handleProfileClick}
            onNavigateToRequest={handleNavigateToRequest}
            onNavigateToPage={handleNavigateToPage}
          />
          <main className="flex-1 scrollable overflow-y-auto mb-4">
            {activePage === 'dashboard' && <DashboardView />}
            {activePage === 'admin-dashboard' && <AdminDashboard />}
            {activePage === 'zeiterfassung' && isPageAccessible('zeiterfassung') && (
              <ZeiterfassungWrapper 
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
              />
            )}
            {activePage === 'auswertung' && isPageAccessible('auswertung') && (
              <ReportsViewNew 
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
              />
            )}
            {activePage === 'meine-anträge' && isPageAccessible('meine-anträge') && <UserRequestsView />}
            {activePage === 'admin' && <AdminUserView />}
            {activePage === 'time-overview' && <TimeOverviewView />}
            {activePage === 'system' && <SystemView />}
            {activePage === 'settings' && <UserSettingsView />}
            {activePage === 'profile' && <UserProfileView />}
            {activePage === 'code' && <ComponentShowcase />}
            {activePage === 'payroll' && <PayrollView />}
            {activePage === 'month-closure' && <MonthClosureView />}
            {activePage === 'user-management' && <UserManagementView />}
            {activePage === 'vehicles' && <VehiclesView />}
            {activePage === 'holidays' && <HolidaysView />}
            {activePage === 'apps' && <AppsView />}
          </main>
        </div>
      </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <NotificationsProvider>
        <Toaster position="bottom-right" />
        <Routes>
          {/* Public route */}
          <Route path="/auth/first-login" element={<FirstLogin />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Main application */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </NotificationsProvider>
    </AuthProvider>
  );
}